import {CSSProperty, EnrichedAstNode} from '../../../types';
import {findClosestParentWithFontSize} from '../../../AstSearchers/FindAst';
import {DEFAULT_EM_VALUE} from '../../constants';
import {extractUnitAndValue} from '../../utils/extractUnitAndValue';
import {convertValueToPixels} from '../../utils/unitConversion';
import {convertFontSizeOfNodeToPixels} from './convertFontSizeOfNodeToPixels';

interface Args {
    cssKey: CSSProperty;
    view: EnrichedAstNode;
    node: EnrichedAstNode;
    parentWidth: number;
    parentHeight: number;
    previewWidth: number;
    closestParentWithFontSize: EnrichedAstNode | null;
}

/**
 * @description When converting a font size value, we need to work up the tree and covert from the first occurence of
 * a pixel font size down. This function is a helper to get us the font size context of the current node so that we can
 * make the conversion.
 */
export const getFontSizeContextInPxForNode = ({
    cssKey,
    view,
    node,
    parentWidth,
    parentHeight,
    previewWidth,
    closestParentWithFontSize,
}: Args): {parent: number; self: number} => {
    const parentNode = closestParentWithFontSize || findClosestParentWithFontSize(view, node.internalId);

    let self = DEFAULT_EM_VALUE;
    let parent = DEFAULT_EM_VALUE;

    const nodeFontSize = node.styles?.fontSize ? extractUnitAndValue(node.styles.fontSize).value : 16;
    const nodeFontSizeUnit = node.styles?.fontSize ? extractUnitAndValue(node.styles.fontSize).unit : 'px';

    if (!parentNode) {
        return {
            parent,
            self: node.styles.fontSize
                ? convertValueToPixels({
                      cssKey,
                      value: nodeFontSize as number,
                      unit: nodeFontSizeUnit,
                      fontSize: self,
                      node,
                      parentFontSize: parent,
                      parentHeight,
                      parentWidth,
                      previewWidth,
                  })
                : self,
        };
    }

    parent = convertFontSizeOfNodeToPixels(view, findClosestParentWithFontSize(view, node.internalId));
    self = parent;

    if (node.styles.fontSize) {
        self = convertFontSizeOfNodeToPixels(view, node, parent);
    }

    return {parent, self};
};
